import { IShareContentRecipient } from '@tableau/share-dialog';
import { ButtonType, ButtonWidget, DialogPositioningBehavior, DialogType, TabStyles } from '@tableau/widgets';
import React from 'react';

import { setExplicitPermissionsForGrantees } from '~permissions/requests/ExplicitPermissions';
import { CapabilityValue, ICapabilityValue } from '~permissions/requests/types/ICapabilityValue';
import { Capabilities } from '~permissions/types/Capabilities';
import { setCollectionVisibility } from '~src/app/actions/spaces/Collections/SetCollectionVisibilityAction';
import { getAppStore } from '~src/app/AppStore';
import { rejectIfResultContainsError } from '~src/httpClient/utils/HttpErrorChecker';
import { EditPermissionsAction } from '~src/permissions/services/EditPermissionsAction';
import { ExplorableItemType } from '~src/ts/models/ContentType';
import { IContentInfo } from '~src/ts/models/IContentInfo';
import { ITypedId } from '~src/ts/ServerApi/types/ITypedId';
import { ToasterService, ToastType } from '~src/ts/services/ToasterService';

import { Formatters, Messages } from '../../../codegen/Localize';
import { ModalInstance, ReactModalOptions, ReactModalService } from '../../../commonInfra/dialogs/ReactModalService';
import { Set } from '../../../ts/models/Set';
import { CollectionVisibility } from './CollectionModels';
import { CollectionsDialogStyles } from './CollectionsDialogStyles';

export namespace GrantPermissionsAfterSharingDialog {
  const DIALOG_WIDTH = 500;
  export const DIALOG_TESTID = 'collection-grant-access';
  export const MANAGE_PERMISSIONS_BUTTON_TESTID = DIALOG_TESTID + '-manage-permissions';
  export const DISMISS_BUTTON_TESTID = DIALOG_TESTID + '-dismiss';
  export const GRANT_ACCESS_BUTTON_TESTID = DIALOG_TESTID + '-grant-access';

  export interface ContentsProps {
    content: IContentInfo<ExplorableItemType>;
    shareRecipients: IShareContentRecipient[];
    unpermissionedRecipients: ITypedId[];
  }

  export const enum DialogResult {
    Dismiss = 'dismiss',
    GrantPermissions = 'grant',
    ManagePermissions = 'managePermissions'
  }

  export const Contents = (props: ContentsProps) => {
    return (
      <div style={{ fontWeight: 'normal', fontSize: '12px', lineHeight: '18px', color: '#4F4F4F', letterSpacing: '0.2px' }}>
        {props.shareRecipients.length === 1 &&
          Messages.collectionsGrantPermissionsDialogSharedWithOneUserMessage({
            COLLECTION_NAME: props.content.name
          })
        }
        {props.shareRecipients.length !== 1 &&
          Messages.collectionsGrantPermissionsDialogMessage({
            COLLECTION_NAME: props.content.name,
            TOTAL_USER_COUNT: props.shareRecipients.length,
            TOTAL_USER_COUNT_LOC: Formatters.formatCount(props.shareRecipients.length),
            UNPERMISSIONED_USER_COUNT: props.unpermissionedRecipients.length,
            UNPERMISSIONED_USER_COUNT_LOC: Formatters.formatCount(props.unpermissionedRecipients.length)
          })}
      </div>
    );
  };

  interface FooterProps {
    onManagePermissions: () => void;
    onDismiss: () => void;
    onGrantAccess: () => void;
  }
  export const Footer = (props: FooterProps) => {
    return (
      <div data-tb-test-id={`${DIALOG_TESTID}-footer`} className={CollectionsDialogStyles.GrantPermissionsDialogFooter}>
        <span className={CollectionsDialogStyles.GrantPermissionsManagePermissionsButton}>
          <ButtonWidget
            handleClick={props.onManagePermissions}
            buttonType={ButtonType.LowEmphasis}
            children={Messages.collectionsGrantPermissionsDialogManagePermissionsAction()}
            testId={MANAGE_PERMISSIONS_BUTTON_TESTID}
          />
        </span>
        <span className={CollectionsDialogStyles.GrantPermissionsRightButtons}>
          <div style={{paddingLeft: TabStyles.Spacing.MediumSpace}}>
            <ButtonWidget
              handleClick={props.onDismiss}
              buttonType={ButtonType.Outline}
              children={Messages.collectionsGrantPermissionsDialogDismiss()}
              testId={DISMISS_BUTTON_TESTID}
            />
          </div>
          <div style={{paddingLeft: TabStyles.Spacing.MediumSpace}}>
            <ButtonWidget
              handleClick={props.onGrantAccess}
              buttonType={ButtonType.Primary}
              children={Messages.collectionsGrantPermissionsDialogGrantAccess()}
              testId={GRANT_ACCESS_BUTTON_TESTID}
            />
          </div>
        </span>
      </div>);
  };

  export function Options(
    footerProps: FooterProps,
    contentProps: ContentsProps): ReactModalOptions {
    return {
      title: Messages.collectionsGrantPermissionsDialogTitle(),
      testId: DIALOG_TESTID,
      type: DialogType.NormalWithTitleBar,
      showCloseButton: true,
      positioningBehavior: DialogPositioningBehavior.Centered,
      dialogWidth: DIALOG_WIDTH,
      dialogBodyStyle: { maxWidth: DIALOG_WIDTH + 'px' },
      dialogContent: <Contents {...contentProps} />,
      dialogContentStyle: { padding: 0 },
      footerContent: <Footer {...footerProps} />
    };
  }

  export function open(props: ContentsProps) {
    let modalInstance: ModalInstance<DialogResult>;

    const onManagePermissions = (): void => {
      const contentAsSet = Set.singletonForIdItem(props.content);
      EditPermissionsAction.showEditPermissionsDialog(contentAsSet, props.content.contentType);
      modalInstance && modalInstance.close(DialogResult.ManagePermissions);
    };

    const onDismiss = (): void => {
      modalInstance && modalInstance.close(DialogResult.Dismiss);
    };

    const onGrantAccess = () => {
      const authorizable: ITypedId = { type: props.content.contentType, id: props.content.id };
      const allowRead: ICapabilityValue = { capability: Capabilities.read, value: CapabilityValue.Allowed };

      const onSuccess = () => {
        ToasterService.instance.pop({
          type: ToastType.Success,
          text: Messages.collectionsGrantPermissionsDialogGrantSuccessMsg({
            USER_COUNT: props.unpermissionedRecipients.length,
            USER_COUNT_LOC: Formatters.formatCount(props.unpermissionedRecipients.length)
          })
        });

        // Due to slowness in BE updates we optimistically update collection visibility label in redux
        // after we've successfully updated permissions.
        // Reminder private just means there are no explicit permissions set.
        if (props.content.contentType === 'collection') {
          getAppStore().dispatch(setCollectionVisibility([props.content.id], CollectionVisibility.public));
        }
      };

      const showErrorToast = () => ToasterService.instance.pop({
        type: ToastType.Error,
        text: Messages.collectionsGrantPermissionsDialogGrantErrorMsg({
          USER_COUNT: props.unpermissionedRecipients.length,
          USER_COUNT_LOC: Formatters.formatCount(props.unpermissionedRecipients.length)
        })
      });

      setExplicitPermissionsForGrantees(authorizable, props.unpermissionedRecipients, [allowRead])
        .then(rejectIfResultContainsError)
        .then(onSuccess, showErrorToast)
        .finally(() => modalInstance?.close(DialogResult.GrantPermissions));
    };

    modalInstance = ReactModalService.open(Options(
      { onManagePermissions, onDismiss, onGrantAccess },
      props
    ));
    return modalInstance.result;
  }
}
